import { cn } from '@/lib/cn';

const TONES: Record<string, { dot: string; text: string; ring: string }> = {
  active: { dot: 'bg-success', text: 'text-success', ring: 'border-success/30 bg-success/5' },
  paused: { dot: 'bg-warning', text: 'text-warning', ring: 'border-warning/30 bg-warning/5' },
  archived: { dot: 'bg-fg-subtle', text: 'text-fg-muted', ring: 'border-border bg-surface-2' },
};

export const STATUS_OPTIONS = ['active', 'paused', 'archived'] as const;

/**
 * Status as a first-class control. Read-only for clients; staff get an inline
 * select disguised as the pill, so changing status is one click in place.
 */
export function StatusPill({
  status,
  onChange,
  busy,
}: {
  status: string;
  onChange?: (next: string) => void;
  busy?: boolean;
}) {
  const tone = TONES[status] ?? TONES.archived!;
  if (!onChange) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium capitalize',
          tone.ring,
          tone.text,
        )}
      >
        <span className={cn('h-1.5 w-1.5 rounded-full', tone.dot)} />
        {status}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'relative inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium capitalize',
        tone.ring,
        tone.text,
        busy && 'opacity-50',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', tone.dot)} />
      {status}
      <svg viewBox="0 0 8 5" className="h-1.5 w-2 opacity-70" fill="currentColor" aria-hidden>
        <path d="M0 0h8L4 5z" />
      </svg>
      <select
        value={status}
        disabled={busy}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => e.stopPropagation()}
        className="absolute inset-0 cursor-pointer opacity-0"
        aria-label="Change status"
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    </span>
  );
}
